import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Code,
  Link,
  Paragraph,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
} from '@customerio/pluma-components/react';

type EventNameMatchingTooltipProps = {
  eventName: string;
  children: ReactNode;
};

function hasSpecialCharacters(eventName: string): boolean {
  return /[|+*]/.test(eventName);
}

/**
 * Warns that `|`, `+`, and `*` carry special meaning in event name matching.
 * Wraps the event-name input and only arms once the current name contains one
 * of those characters.
 *
 * The Popover stays mounted whether or not the warning is armed — arming is
 * gated through the controlled `isOpen` instead. Returning bare `children`
 * while unarmed would change the element type at this tree position on the
 * keystroke that adds a special character, remounting the input mid-word and
 * dropping focus. The Ember editor keeps its tooltip mounted the same way
 * (`PlumaTooltip @isDisabled`).
 */
export function EventNameMatchingTooltip({ eventName, children }: EventNameMatchingTooltipProps) {
  // Hover/focus intent from the popover's own triggers; the warning renders
  // only while that intent coincides with a special character in the name, and
  // closes immediately when the character is deleted.
  const [isTriggered, setIsTriggered] = useState(false);
  return (
    <Popover
      shouldTriggerOnHover
      shouldTriggerOnClick={false}
      placement="top"
      initialFocus={-1}
      isOpen={hasSpecialCharacters(eventName) && isTriggered}
      onOpenChange={setIsTriggered}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent>
        <Stack gap="200">
          <Paragraph>
            Characters <Code>|</Code>, <Code>+</Code>, and <Code>*</Code> have special meaning in event name matching
            and may need to be escaped.
          </Paragraph>
          <Paragraph>
            <Link
              isUnderlined
              href="https://docs.customer.io/journeys/data-driven-segments/#special-characters"
              isExternal
            >
              Learn more about special characters
            </Link>
          </Paragraph>
        </Stack>
      </PopoverContent>
    </Popover>
  );
}
